import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import ktkService from "../../services/ktkService";
import ktkRestrictService from "../../services/ktkRestrictService";

const initialState = {
  channelsList: [],
  kidChannel: [],
  channelVideo: [],
};

export const get_channels_list = createAsyncThunk("ktk/getChannels", () => {
  return ktkService.getChannelsList().then((response) => response.data);
});

export const get_restrict_channels_list = createAsyncThunk("ktk/getRestrictChannels", (userId) => {
  return ktkRestrictService.getChannelsList(userId).then((response) => response.data);
});

export const get_more_channels_list = createAsyncThunk(
  "ktk/getMoreChannels",
  (page) => {
    return ktkService
      .getMoreChannelsList(page)
      .then((response) => response.data);
  }
);

export const get_more_restrict_channels_list = createAsyncThunk(
  "ktk/getMoreRestrictChannels",
  ({userId, page}) => {
    return ktkRestrictService
      .getMoreChannelsList(userId, page)
      .then((response) => response.data);
  }
);

export const get_kid_channel = createAsyncThunk(
  "ktk/getKidChannel",
  (channelId) => {
    return ktkService
      .getKidChannel(channelId)
      .then((response) => response.data);
  }
);

export const get_restrict_kid_channel = createAsyncThunk(
  "ktk/getRestrictKidChannel",
  ({userId, channelId}) => {
    return ktkRestrictService
      .getKidChannel(userId, channelId)
      .then((response) => response.data);
  }
);

export const get_more_kid_channel = createAsyncThunk(
  "ktk/getMoreKidChannel",
  ({ channelId, page }) => {
    return ktkService
      .getMoreKidChannel(channelId, page)
      .then((response) => response.data);
  }
);

export const get_more_restrict_kid_channel = createAsyncThunk(
  "ktk/getMoreRestrictKidChannel",
  ({ userId, channelId, page }) => {
    return ktkRestrictService
      .getMoreKidChannel(userId, channelId, page)
      .then((response) => response.data);
  }
);

export const get_channel_video = createAsyncThunk(
  "ktk/getChannelVideo",
  // payload needs to include userId for the manager path
  async ({ userId, channelId, videoId }, { getState }) => {
    const { account_level } = getState().user.userInfo;

    if (account_level === "manager") {
      // parent/manager path
      const resp = await ktkRestrictService.getChannelVideo(userId, channelId, videoId);
      return resp.data;
    } else {
      // student path
      const resp = await ktkService.getChannelVideo(channelId, videoId);
      return resp.data;
    }
  }
);

export const get_restrict_channel_video = createAsyncThunk(
  "ktk/getRestrictChannelVideo",
  ({ userId, channelId, videoId }) => {
    return ktkRestrictService
      .getChannelVideo(userId, channelId, videoId)
      .then((response) => response.data);
  }
);
export const add_channel_subscriber = createAsyncThunk(
  "ktk/addChannelSubscriber",
  async (channelId) => {
    const response = await ktkService.addChannelSubscriber(channelId);
    return response.data; // contains subscriber_count and is_subscribed
  }
);

export const add_channel_video_comment = createAsyncThunk(
  "ktk/add_channel_video_comment",
  async ({ channelId, videoId, text, parent}) => {
    const response = await ktkService.addChannelVideoComment(channelId, videoId, text, parent);
    return response.data;
  }
);

export const delete_channel_video_comment = createAsyncThunk(
  "ktk/delete_channel_video_comment",
  async ({ channelId, videoId, commentId }) => {
    await ktkService.deleteChannelVideoComment(channelId, videoId, commentId);
    return commentId;
  }
);

export const report_channel_video_comment = createAsyncThunk(
  "ktk/report_channel_video_comment",
  async ({ channelId, videoId, commentId }) => {
    await ktkService.reportChannelVideoComment(channelId, videoId, commentId);
    return commentId;
  }
);


export const get_channel_by_user = createAsyncThunk(
  'ktk/get_channel_by_user',
  async (userId) => {
    const resp = await ktkService.getChannelByUser(userId);
    // assuming the API returns an array and you want the first match:
    return resp.data[0];
  }
);

const ktkSlice = createSlice({
  name: "ktk",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(get_channels_list.fulfilled, (state, action) => {
      state.channelsList = action.payload;
    });
    builder.addCase(get_more_channels_list.fulfilled, (state, action) => {
      state.channelsList.current_page = action.payload.current_page;
      state.channelsList.next_count = action.payload.next_count;
      state.channelsList.next_page = action.payload.next_page;
      state.channelsList.data = state.channelsList.data.concat(
        action.payload.data
      );
    });
    builder.addCase(get_kid_channel.fulfilled, (state, action) => {
      state.kidChannel = action.payload;
    });
    builder.addCase(get_more_kid_channel.fulfilled, (state, action) => {
      state.kidChannel.current_page = action.payload.current_page;
      state.kidChannel.next_count = action.payload.next_count;
      state.kidChannel.next_page = action.payload.next_page;
      state.kidChannel.data.videos = state.kidChannel.data.videos.concat(
        action.payload.data.videos
      );
    });
    builder.addCase(get_channel_video.fulfilled, (state, action) => {
      state.channelVideo = action.payload;
    });

    builder.addCase(get_restrict_channels_list.fulfilled, (state, action) => {
      state.channelsList = action.payload;
    });
    builder.addCase(get_restrict_kid_channel.fulfilled, (state, action) => {
      state.kidChannel = action.payload;
    });
    builder.addCase(get_restrict_channel_video.fulfilled, (state, action) => {
      state.channelVideo = action.payload;
    });
    builder.addCase(get_more_restrict_kid_channel.fulfilled, (state, action) => {
      state.kidChannel.current_page = action.payload.current_page;
      state.kidChannel.next_count = action.payload.next_count;
      state.kidChannel.next_page = action.payload.next_page;
      state.kidChannel.data.videos = state.kidChannel.data.videos.concat(
        action.payload.data.videos
      );
    });
    builder.addCase(get_more_restrict_channels_list.fulfilled, (state, action) => {
      state.channelsList.current_page = action.payload.current_page;
      state.channelsList.next_count = action.payload.next_count;
      state.channelsList.next_page = action.payload.next_page;
      state.channelsList.data = state.channelsList.data.concat(
        action.payload.data
      );
    });
  },
});

export default ktkSlice.reducer;
